import {
  IonButton,
  IonContent,
  IonInput,
  IonPage,
  IonSpinner,
  IonTextarea,
  useIonToast,
} from "@ionic/react";
import { arrowBackOutline } from "ionicons/icons";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Header from "src/components/Header";
import {
  BuscadorRemoto,
  campoEstilo,
  Etiqueta,
  fmtFecha,
  isoHoyMas,
  MultiSelect,
  RadioOpciones,
  SI_NO,
  TablaPersonas,
} from "src/components/SolicitudCapacitacionCampos";
import SolicitudCapacitacionAPI, {
  CierrePayload,
  DatosCierre,
  OperarioBusqueda,
  OpcionCatalogo,
  SolicitudCapacitacion,
  TemaApp,
} from "src/services/solicitudCapacitacion";

const SI_NO_NA = [...SI_NO, { valor: "NA", etiqueta: "No Aplica" }];

const VOLVER = "/solicitudes-capacitacion?tab=asignadas";

const bordeInput: React.CSSProperties = { border: "1px solid #ccc", borderRadius: 4, paddingLeft: 8 };
const bordeTextarea: React.CSSProperties = { border: "1px solid #ccc", borderRadius: 4, padding: "0 8px" };

/**
 * Pantalla de cierre de la capacitación (/solicitudes-capacitacion/:id/cierre)
 * para el capacitador asignado. Si se realizó, el backend la registra en
 * AE_C_Capacitaciones (igual que "Registro de Capacitación") por cada
 * capacitado marcado y tema impartido.
 *
 * Capacitados: vienen todos marcados (personas de la solicitud individual, o
 * todos los asignados activos del PS) y el capacitador desmarca a quien no
 * participó.
 */
const SolicitudCapacitacionCierre: React.FC = () => {
  const [present] = useIonToast();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();

  const [solicitud, setSolicitud] = useState<SolicitudCapacitacion | null>(null);
  const [datos, setDatos] = useState<DatosCierre | null>(null);
  const [debilidadesCatalogo, setDebilidadesCatalogo] = useState<OpcionCatalogo[]>([]);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  const [realizada, setRealizada] = useState<string | null>(null);
  const [motivo, setMotivo] = useState("");
  const [fechaRealizada, setFechaRealizada] = useState("");
  const [temas, setTemas] = useState<TemaApp[]>([]);
  const [capacitados, setCapacitados] = useState<OperarioBusqueda[]>([]);
  // Participantes que todavía no figuran asignados en el sistema (la
  // asignación cambió en la realidad y falta actualizarla en EMSYS).
  const [agregados, setAgregados] = useState<OperarioBusqueda[]>([]);
  const [elementos, setElementos] = useState<string | null>(null);
  const [supervisor, setSupervisor] = useState<OperarioBusqueda | null>(null);
  const [habilidades, setHabilidades] = useState<string | null>(null);
  const [preocupacion, setPreocupacion] = useState<string | null>(null);
  const [receptivos, setReceptivos] = useState<string | null>(null);
  const [debilidades, setDebilidades] = useState<OpcionCatalogo[]>([]);
  const [calidad, setCalidad] = useState<string | null>(null);
  const [guantes, setGuantes] = useState<string | null>(null);
  const [comentarios, setComentarios] = useState("");

  const hoy = isoHoyMas(0);
  const esPS = solicitud?.tipo === "PS";
  const candidatos = datos?.operarios_ps || [];

  useEffect(() => {
    const solicitudId = Number(id);
    if (!solicitudId) return;
    setCargando(true);
    setRealizada(null);
    setMotivo("");
    setAgregados([]);
    setElementos(null);
    setSupervisor(null);
    setHabilidades(null);
    setPreocupacion(null);
    setReceptivos(null);
    setDebilidades([]);
    setCalidad(null);
    setGuantes(null);
    setComentarios("");
    Promise.all([
      SolicitudCapacitacionAPI.obtener(solicitudId),
      SolicitudCapacitacionAPI.datosCierre(solicitudId),
      SolicitudCapacitacionAPI.catalogo(),
    ])
      .then(([rs, rd, rc]) => {
        const s = rs.data.datos;
        const d = rd.data.datos;
        setSolicitud(s);
        setFechaRealizada(s.fecha_programada || "");
        setDatos(d);
        setTemas(d.temas.filter((t) => d.temas_sugeridos.includes(t.Id)));
        setCapacitados(d.operarios_ps || []);
        setDebilidadesCatalogo(rc.data.datos.temas || []);
      })
      .catch((err) =>
        present({ message: err?.response?.data?.error || "No se pudo preparar el cierre", duration: 4000, color: "danger" })
      )
      .finally(() => setCargando(false));
  }, [id, present]);

  const cerrable =
    !!solicitud &&
    (solicitud.estado === "PROGRAMADA" || solicitud.estado === "PENDIENTE_CIERRE") &&
    !!solicitud.fecha_programada &&
    solicitud.fecha_programada < hoy;

  const alternar = (o: OperarioBusqueda, valor: boolean) =>
    setCapacitados((prev) =>
      valor
        ? prev.some((c) => c.legajo === o.legajo)
          ? prev
          : [...prev, o]
        : prev.filter((c) => c.legajo !== o.legajo)
    );

  const validar = (): string | null => {
    if (!realizada) return "Indique si la capacitación fue realizada";
    if (realizada === "NO") return motivo.trim() ? null : "Indique el motivo por el que no se realizó";
    if (!fechaRealizada || fechaRealizada > hoy) return "Indique la fecha de realización (no futura)";
    if (!temas.length) return "Seleccione los temas impartidos";
    if (!capacitados.length && !agregados.length) return "Marque o agregue al menos una persona que participó";
    if (!elementos || !habilidades || !preocupacion || !receptivos || !calidad || !guantes)
      return "Complete todas las preguntas obligatorias";
    if (!comentarios.trim()) return "Complete los comentarios sobre los colaboradores";
    return null;
  };

  const guardar = async () => {
    if (!solicitud) return;
    const error = validar();
    if (error) {
      present({ message: error, duration: 3000, color: "warning" });
      return;
    }
    const payload: CierrePayload =
      realizada === "NO"
        ? { realizada: false, motivo_no_realizada: motivo.trim() }
        : {
            realizada: true,
            fecha_realizada: fechaRealizada,
            temas: temas.map((t) => t.Id),
            capacitados: capacitados.map((c) => Number(c.legajo)),
            adicionales: agregados.map((c) => Number(c.legajo)),
            elementos_basicos: elementos!,
            supervisor_nombre: supervisor ? `${supervisor.legajo} - ${supervisor.nombre}` : undefined,
            supervisor_habilidades: habilidades!,
            supervisor_preocupacion: preocupacion!,
            colaboradores_receptivos: receptivos!,
            debilidades: debilidades.map((d) => d.id),
            calidad_servicio: calidad!,
            usan_guantes: guantes === "SI",
            comentarios_cierre: comentarios.trim(),
          };
    setGuardando(true);
    try {
      const r = await SolicitudCapacitacionAPI.cerrar(solicitud.solicitud_id, payload);
      present({ message: r.data.info || "Capacitación cerrada", duration: 3000, color: "success" });
      history.replace(VOLVER);
    } catch (err: any) {
      present({ message: err?.response?.data?.error || "No se pudo cerrar la capacitación", duration: 4000, color: "danger" });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <IonPage>
      <Header titulo={`Cierre N° ${id}`} botonIzq={{ icono: arrowBackOutline, url: VOLVER }} />
      <IonContent id="solicitud-capacitacion-cierre">
        <div style={{ padding: 16, maxWidth: 800, margin: "0 auto", boxSizing: "border-box" }}>
          {solicitud && (
            <p style={{ fontSize: 14, color: "#555", marginTop: 0 }}>
              <strong>{solicitud.ps_nombre}</strong>
              <br />
              {esPS ? "Por Punto de Servicio" : "Individual"} · programada {fmtFecha(solicitud.fecha_programada)}{" "}
              {solicitud.hora_programada || ""}
            </p>
          )}

          {cargando ? (
            <div style={{ textAlign: "center", padding: 24 }}>
              <IonSpinner />
            </div>
          ) : !cerrable ? (
            <p style={{ textAlign: "center", color: "#777", padding: 24 }}>
              {solicitud?.estado === "CERRADA"
                ? "Esta capacitación ya fue cerrada."
                : "El cierre se habilita el día siguiente a la capacitación programada."}
            </p>
          ) : (
            <>
              <RadioOpciones
                etiqueta="Capacitación Realizada"
                obligatorio
                opciones={SI_NO}
                valor={realizada}
                onChange={setRealizada}
                disabled={guardando}
              />

              {realizada === "NO" && (
                <div style={campoEstilo}>
                  <Etiqueta texto="Motivo por el que no se realizó" obligatorio />
                  <IonTextarea
                    value={motivo}
                    rows={3}
                    autoGrow
                    maxlength={1000}
                    disabled={guardando}
                    onIonChange={(e) => setMotivo(e.detail.value || "")}
                    style={bordeTextarea}
                  />
                </div>
              )}

              {realizada === "SI" && datos && (
                <>
                  <div style={campoEstilo}>
                    <Etiqueta texto="Fecha en que se realizó" obligatorio />
                    <IonInput
                      type="date"
                      value={fechaRealizada}
                      max={hoy}
                      disabled={guardando}
                      onIonChange={(e) => setFechaRealizada(e.detail.value || "")}
                      style={bordeInput}
                    />
                  </div>

                  <MultiSelect<TemaApp>
                    etiqueta="Temas impartidos"
                    obligatorio
                    opciones={datos.temas}
                    valor={temas}
                    onChange={setTemas}
                    getId={(t) => t.Id}
                    getLabel={(t) => t.Tema}
                    groupBy={(t) => t.Categoria}
                    ayuda="Se registran en el historial de capacitación de cada capacitado. Vienen precargados según lo solicitado."
                    disabled={guardando}
                  />

                  <div style={campoEstilo}>
                    <Etiqueta
                      texto={`Capacitados (${capacitados.length + agregados.length} de ${candidatos.length + agregados.length})`}
                      obligatorio
                    />
                    <small style={{ color: "#777", display: "block", marginBottom: 6 }}>
                      {esPS
                        ? "Asignados activos del PS. Desmarcá a quien no participó."
                        : "Personas de la solicitud. Desmarcá a quien no participó."}
                    </small>
                    {candidatos.length === 0 && (
                      <span style={{ color: "#c00", display: "block", marginBottom: 6 }}>
                        {esPS ? "El PS no tiene asignados activos." : "La solicitud no tiene personas."}
                      </span>
                    )}
                    {candidatos.length > 0 && (
                      <div style={{ display: "flex", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                        <IonButton size="small" fill="outline" disabled={guardando} onClick={() => setCapacitados(candidatos)}>
                          Marcar todos
                        </IonButton>
                        <IonButton
                          size="small"
                          fill="outline"
                          color="medium"
                          disabled={guardando}
                          onClick={() => setCapacitados([])}
                        >
                          Desmarcar todos
                        </IonButton>
                      </div>
                    )}
                    {candidatos.length + agregados.length > 0 && (
                      <TablaPersonas
                        personas={[...candidatos, ...agregados.map((a) => ({ ...a, etiqueta: "Agregado" }))]}
                        seleccion={{
                          marcado: (legajo) =>
                            capacitados.some((c) => c.legajo === legajo) || agregados.some((a) => a.legajo === legajo),
                          alternar: (legajo, valor) => {
                            const o = candidatos.find((c) => c.legajo === legajo);
                            if (o) alternar(o, valor);
                            // Un agregado desmarcado se quita de la lista.
                            else if (!valor) setAgregados((prev) => prev.filter((a) => a.legajo !== legajo));
                          },
                          disabled: guardando,
                        }}
                      />
                    )}

                    <div style={{ marginTop: 10 }}>
                      <Etiqueta texto="Agregar a alguien que participó y no figura en la lista" />
                      <BuscadorRemoto<OperarioBusqueda>
                        etiqueta="Buscar por legajo o nombre y agregar"
                        valor={null}
                        onChange={(o) => {
                          if (!o) return;
                          const enLista = candidatos.find((c) => c.legajo === o.legajo);
                          if (enLista) {
                            alternar(enLista, true); // ya estaba: solo se marca
                          } else {
                            setAgregados((prev) => (prev.some((a) => a.legajo === o.legajo) ? prev : [...prev, o]));
                          }
                        }}
                        buscar={async (q) => (await SolicitudCapacitacionAPI.operarios(q)).data.datos || []}
                        getLabel={(o) => `${o.legajo} - ${o.nombre}${o.cargo ? ` (${o.cargo})` : ""}`}
                        getId={(o) => o.legajo}
                        minimo={1}
                        disabled={guardando}
                        limpiarAlElegir
                      />
                      <small style={{ color: "#777" }}>
                        Para casos en que la asignación ya cambió en la realidad pero todavía no en el sistema.
                      </small>
                    </div>
                  </div>

                  <RadioOpciones
                    etiqueta="¿En el PS tienen todos los elementos/equipos básicos?"
                    obligatorio
                    opciones={[
                      { valor: "SIEMPRE", etiqueta: "Siempre" },
                      { valor: "CASI_SIEMPRE", etiqueta: "Casi Siempre" },
                      { valor: "RARAS_VECES", etiqueta: "Raras Veces" },
                      { valor: "NUNCA", etiqueta: "Nunca" },
                    ]}
                    valor={elementos}
                    onChange={setElementos}
                    disabled={guardando}
                  />

                  <div style={campoEstilo}>
                    <Etiqueta texto="Nombre y apellido del supervisor/a" />
                    <BuscadorRemoto<OperarioBusqueda>
                      etiqueta="Buscar por legajo o nombre"
                      valor={supervisor}
                      onChange={setSupervisor}
                      buscar={async (q) => (await SolicitudCapacitacionAPI.operarios(q)).data.datos || []}
                      getLabel={(o) => `${o.legajo} - ${o.nombre}${o.cargo ? ` (${o.cargo})` : ""}`}
                      getId={(o) => o.legajo}
                      minimo={1}
                      disabled={guardando}
                    />
                    <small style={{ color: "#777" }}>
                      Opcional. Incluye operarios, porque hay supervisores que todavía figuran con cargo de operario.
                    </small>
                  </div>

                  <RadioOpciones
                    etiqueta="¿Considerás que el Supervisor posee las habilidades y experiencia necesaria para desempeñar efectivamente su rol?"
                    obligatorio
                    opciones={SI_NO_NA}
                    valor={habilidades}
                    onChange={setHabilidades}
                    disabled={guardando}
                  />
                  <RadioOpciones
                    etiqueta="¿El Supervisor demuestra preocupación por las necesidades del punto de servicio?"
                    obligatorio
                    opciones={SI_NO_NA}
                    valor={preocupacion}
                    onChange={setPreocupacion}
                    disabled={guardando}
                  />
                  <RadioOpciones
                    etiqueta="¿Los colaboradores toman positivamente las capacitaciones impartidas por el equipo de CT?"
                    obligatorio
                    opciones={[...SI_NO, { valor: "ALGUNOS", etiqueta: "Solo Algunos" }]}
                    valor={receptivos}
                    onChange={setReceptivos}
                    disabled={guardando}
                  />

                  <MultiSelect<OpcionCatalogo>
                    etiqueta="Principales Debilidades Detectadas"
                    opciones={debilidadesCatalogo}
                    valor={debilidades}
                    onChange={setDebilidades}
                    getId={(o) => o.id}
                    getLabel={(o) => o.nombre}
                    ayuda="Puede seleccionar más de una opción"
                    disabled={guardando}
                  />

                  <RadioOpciones
                    etiqueta="En líneas generales ¿Cómo calificás la CALIDAD del servicio de limpieza en el Punto de Servicio?"
                    obligatorio
                    opciones={[
                      { valor: "DEFICIENTE", etiqueta: "Deficiente" },
                      { valor: "REGULAR", etiqueta: "Regular" },
                      { valor: "BUENO", etiqueta: "Bueno" },
                      { valor: "MUY_BUENO", etiqueta: "Muy Bueno" },
                      { valor: "EXCELENTE", etiqueta: "Excelente" },
                    ]}
                    valor={calidad}
                    onChange={setCalidad}
                    disabled={guardando}
                  />
                  <RadioOpciones
                    etiqueta="Los colaboradores asignados al Punto de Servicio, ¿utilizan guantes para el trabajo diario?"
                    obligatorio
                    opciones={SI_NO}
                    valor={guantes}
                    onChange={setGuantes}
                    disabled={guardando}
                  />

                  <div style={campoEstilo}>
                    <Etiqueta texto="Comentarios sobre los Colaboradores / Sugerencias / Oportunidad de Mejora" obligatorio />
                    <IonTextarea
                      value={comentarios}
                      rows={4}
                      autoGrow
                      maxlength={1000}
                      disabled={guardando}
                      onIonChange={(e) => setComentarios(e.detail.value || "")}
                      style={bordeTextarea}
                    />
                  </div>
                </>
              )}

              <IonButton expand="block" onClick={guardar} disabled={guardando || !realizada}>
                {guardando ? "Guardando..." : "Cerrar capacitación"}
              </IonButton>
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default SolicitudCapacitacionCierre;
